import React from "react"
import { TagIcon } from "@heroicons/react/24/outline"
import Popover from "~/ui-rtk/components/ui/common/Popover"
import { formatTagLabel } from "~/ui-rtk/types/structured-tags"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

const TAG_COLOR_BG: Record<string, string> = {
  red: "bg-system-error-100 text-system-error-800",
  orange: "bg-system-orange-100 text-system-orange-800",
  yellow: "bg-system-yellow-100 text-system-yellow-800",
  green: "bg-system-success-100 text-system-success-800",
  blue: "bg-system-blue-100 text-system-blue-800",
  indigo: "bg-system-sea-100 text-system-sea-800",
  purple: "bg-system-purple-100 text-system-purple-800",
  pink: "bg-system-brand-pink-100 text-system-brand-pink-800",
  gray: "bg-system-gray-100 text-system-gray-800",
}

export interface TagPill {
  label: string
  color: string
  categoryLabel?: string
}

interface TagPillGroupProps {
  /** Pre-resolved pills with labels and colors (no category lookup needed) */
  pills: TagPill[]
  onClick?: () => void
  className?: string
  /**
   * When true, the bulk-get for this row's tag assignments hasn't resolved
   * yet. Render a spinner instead of the "Add Tag" empty state so users
   * don't briefly see the empty CTA on a row that actually has tags.
   */
  isLoading?: boolean
}

const MAX_VISIBLE = 3

/**
 * Compact inline display of assigned tags for table cells.
 * Shows up to 3 pills inline. Overflow shows a popover with all tags.
 * Clicking anywhere opens the TagPanel for editing.
 */
export const TagPillGroup: React.FC<TagPillGroupProps> = ({
  pills,
  onClick,
  className,
  isLoading = false,
}) => {
  const visible = pills.slice(0, MAX_VISIBLE)
  const overflow = pills.length - MAX_VISIBLE

  if (isLoading && pills.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center justify-center w-full px-2.5 py-1.5",
          className,
        )}
        aria-label="Loading tags"
      >
        <span className="loading loading-spinner loading-xs text-system-blue-300" />
      </div>
    )
  }

  if (pills.length === 0) {
    return (
      <button
        onClick={onClick}
        className={cn(
          "flex items-center justify-center gap-1.5 w-full px-2.5 py-1.5 text-xs bg-gradient-to-r from-system-blue-600 to-system-blue-700 border border-system-blue-500 rounded-lg hover:from-system-blue-500 hover:to-system-blue-600 transition-all duration-200 text-white shadow-sm hover:shadow-md cursor-pointer",
          className,
        )}
      >
        <TagIcon className="w-3 h-3" />
        <span className="font-medium">Add Tag</span>
      </button>
    )
  }

  return (
    <div className={cn("flex items-center gap-1 flex-wrap", className)}>
      {visible.map((pill, idx) => (
        <button
          key={idx}
          onClick={onClick}
          className={cn(
            "px-2 py-0.5 rounded text-[10px] font-medium leading-4 truncate max-w-[80px] cursor-pointer hover:opacity-80 transition-opacity",
            TAG_COLOR_BG[pill.color] || TAG_COLOR_BG.blue,
          )}
          title={
            pill.categoryLabel
              ? `${pill.categoryLabel}: ${formatTagLabel(pill.label)}`
              : formatTagLabel(pill.label)
          }
        >
          {formatTagLabel(pill.label)}
        </button>
      ))}

      {overflow > 0 && (
        <Popover
          relative
          align="start"
          handler={() => (
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-system-gray-600 text-system-gray-200 cursor-pointer hover:bg-system-gray-500 transition-colors"
              title="Show all tags"
            >
              +{overflow}
            </span>
          )}
        >
          {() => (
            <div
              className="mt-1 p-3 bg-system-blue-800 border border-system-blue-600/50 rounded-lg shadow-xl min-w-[200px] max-w-[300px]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-system-gray-400 uppercase tracking-wider">
                  All Tags ({pills.length})
                </span>
                <button
                  onClick={onClick}
                  className="text-[10px] text-system-blue-400 hover:text-system-blue-300 font-medium"
                >
                  Edit
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {pills.map((pill, idx) => (
                  <span
                    key={idx}
                    className={cn(
                      "px-1.5 py-0.5 rounded text-[10px] font-medium",
                      TAG_COLOR_BG[pill.color] || TAG_COLOR_BG.blue,
                    )}
                    title={pill.categoryLabel || undefined}
                  >
                    {formatTagLabel(pill.label)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Popover>
      )}
    </div>
  )
}
